import * as React from "react";
import { Dispatch, useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import { QualifiedFeature } from "fe-common/graphql";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { DataInput } from "../../dataInputTypes";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Suggestion } from "../hooks/useSuggestions";
import { ClinicalReasoningIconDesktop } from "./clinicalReasoningIconDesktop";
import { MobileOnly } from "../util/mobileOnly";
import { DesktopOnly } from "../util/desktopOnly";
import { ClinicalReasoningIconMobile } from "./clinicalReasoningIconMobile";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ClinicalReasoningTooltip } from "./ClinicalReasoningTooltip";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { PanelsUtil } from "../util/PanelsUtil";

//region [[ Styles ]]

const ProcedureItemView = styled("div")(({ theme }) => ({
  marginBottom: "10px",
  marginRight: "5px",
  display: "flex",
  flexDirection: "column",
}));

const PlusIcon = styled(AddIcon)(({ theme }) => ({}));

const SelectedIcon = styled(CheckIcon)(({ theme }) => ({}));

export const MobileClinicalReasoning = styled(ClinicalReasoningTooltip)(
  ({ theme }) => ({
    marginBottom: "0px",
    padding: "8px",
    margin: "10px",
    marginLeft: "5px",
    [theme.breakpoints.down(MobileView)]: {
      padding: "0px",
      marginTop: "10px",
      margin: "0px",
    },
  }),
);

const VertDivider = styled("div")(({ theme }) => ({
  backgroundColor: "black",
  width: "2px",
  marginLeft: "15px",
  borderLeft: "2px solid black",
}));

const IconContainer = styled("div")({
  marginLeft: "5px",
  marginRight: "-5px",
  marginTop: "7px",
});

const ProcedureItemRow = styled("div")({
  display: "flex",
  alignItems: "center",
});

const ProcedureClinicalReasoning = styled("div")({
  marginLeft: "15px",
});

const ProcedureName = styled("div")({
  textAlign: "left",
});

const AddArea = styled("div")({
  display: "flex",
});

const ProcedureItemContent = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const ProcedureItemButton = styled("div")(({ theme }) => ({
  minHeight: "36px",
  width: "300px",
  padding: "0px",
  paddingLeft: "25px",
  paddingRight: "15px",
  [theme.breakpoints.down(MobileView)]: {
    width: "360px",
  },
  textTransform: "capitalize",
  display: "flex",
  justifyContent: "space-between",
  boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.14)",
  "&:hover": {
    boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.14)",
    backgroundColor: "white",
    "&.checked": {
      backgroundColor: KahunColorMap.Red,
    },
  },
  color: KahunColorMap.DefaultLight,
  backgroundColor: "white",
  "&.checked": {
    color: "white",
    backgroundColor: KahunColorMap.Red,
  },
  borderRadius: "20px",
  cursor: "pointer",
  transition: "all 0.5s ease-out",
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ProcedureItemProps {
  className?: string;
  qf: QualifiedFeature;
  dataInput?: DataInput;
  onChange: Dispatch<DataInput>;
  onYes?: () => void;
  onNo?: () => void;
  suggestion?: Suggestion;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ProcedureItem = ({
  dataInput,
  qf,
  onYes,
  onNo,
  ...props
}: ProcedureItemProps) => {
  const selected = !dataInput || dataInput.negate;
  const [showClinicalReasoning, setShowClinicalReasoning] = useState(false);

  const onShowClinicalReasoning = useCallback(() => {
    evt({
      category: CATEGORY.PLAN,
      action: showClinicalReasoning
        ? ACTION.HIDE_CLINICAL_REASONING
        : ACTION.SHOW_CLINICAL_REASONING,
      label: qf?.qualifiers[0].value.name,
    });

    setShowClinicalReasoning((prev) => !prev);
  }, [qf, setShowClinicalReasoning, showClinicalReasoning]);

  const onButtonClick = useCallback(() => {
    if (selected) {
      onYes();
    } else {
      onNo();
    }
  }, [selected, onYes, onNo]);

  return (
    <ProcedureItemView>
      <ProcedureItemRow>
        <ProcedureItemButton
          className={selected ? "un-checked" : "checked"}
          onClick={onButtonClick}
        >
          <ProcedureItemContent>
            <ProcedureName>
              {PanelsUtil.addEllipsis(qf?.qualifiers[0].value.name, 50)}
            </ProcedureName>
            <AddArea>
              <VertDivider />
              <IconContainer>
                {selected ? <PlusIcon /> : <SelectedIcon />}
              </IconContainer>
            </AddArea>
          </ProcedureItemContent>
        </ProcedureItemButton>

        {props.suggestion && (
          <>
            <DesktopOnly>
              <ClinicalReasoningIconDesktop
                suggestion={props.suggestion}
                qualifier={qf?.qualifiers[0]}
              />
            </DesktopOnly>
            <MobileOnly>
              <ClinicalReasoningIconMobile
                showClinicalReasoning={showClinicalReasoning}
                onShowClinicalReasoning={onShowClinicalReasoning}
              />
            </MobileOnly>
          </>
        )}
      </ProcedureItemRow>

      {showClinicalReasoning && (
        <ProcedureClinicalReasoning>
          <MobileClinicalReasoning
            show={showClinicalReasoning}
            suggestion={props.suggestion}
            qualifier={qf?.qualifiers[0]}
          />
        </ProcedureClinicalReasoning>
      )}
    </ProcedureItemView>
  );
};
